/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from 'react'
import { Spinner } from '@radix-ui/themes/components/spinner'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from '@tanstack/react-router'
import { IconInfoCircle, IconX } from '@tabler/icons-react'
import { useGetPracTestDetail } from '@/services/pracTestService'
import { practiceTestService } from '@/services/practiceTestService'
import {
  DuplicatePracticeTestBody,
  PracTestDetailRes,
  PracticeTestDetail,
  PracticeTestStatus,
} from '@/types/practice-test'
import { toast } from 'sonner'
import { useAuth } from '@/stores/authStore'
import { hoverPressable } from '@/utils/style'
import { useHeroNavbar } from '@/context/hero-navbar-context'
import Preview from '@/components/ui/PreviewTest/preview'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import Hstack from '@/components/ui/hstack'
import Stack from '@/components/ui/stack'
import { ConfirmDialog } from '@/components/confirm-dialog'
import HeaderEdit from '@/components/layout/header-edit'
import DetailView from './components/detail-view'

export default function MockExamDetail({isLoading}: {isLoading?: boolean}) {
  const { ptId, moduleId } = useParams({
    from: '/_authenticated/create-mock-exam/detail/$ptId/$moduleId',
  }) as { ptId: string; moduleId: string; classId: string }
  const { setIsActive } = useHeroNavbar()
  const [page, _setPage] = useState<number>(1)
  const [size, _setSize] = useState<number>(10)
  const navigate = useNavigate()
  const { user } = useAuth()
  const [isDuplicateOpen, setIsDuplicateOpen] = useState<boolean>(false)
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false)
  const [isConfirmActiveOpen, setIsConfirmActiveOpen] = useState(false)

  const { pracTestStat, isLoadingPracTestStat } = useGetPracTestDetail(
    ptId ?? ''
  )

  useEffect(() => {
    setIsActive(false)
    return () => setIsActive(true)
  }, [setIsActive])

  const { data: practiceTestDetailData } = useQuery({
    queryKey: ['practiceTestDetail', ptId, size, page],
    queryFn: () =>
      practiceTestService.getPracticeTest({
        practice_test_id: ptId,
        size,
        page,
      }),
  })

  const { mutateAsync: deactivatePracticeTest, isPending: isDeactiving } =
    useMutation({
      mutationFn: (practiceTestId: string) =>
        practiceTestService.unpublishPracticeTest(practiceTestId),
    })

  const { mutateAsync: activatePracticeTest, isPending: isActiving } =
    useMutation({
      mutationFn: (practiceTestId: string) =>
        practiceTestService.publishPracticeTest(practiceTestId),
    })

  const { mutateAsync: duplicatePracticeTest, isPending: isDuplicating } =
    useMutation({
      mutationFn: (body: DuplicatePracticeTestBody) =>
        practiceTestService.duplicatePracticeTest(body),
    })

  const totalDoingTimeSec = useMemo(() => {
    const secs = (practiceTestDetailData?.sections || []).reduce(
      (acc: number, s: any) => acc + (Number(s?.total_time) || 0),
      0
    )
    // backend seems to use minutes in many places; adjust here if needed
    // If total_time is minutes, multiply by 60. Assuming seconds for now.
    return secs
  }, [practiceTestDetailData])

  const canDuplicateTest = useMemo(() => {
    if (!practiceTestDetailData) {
      return false
    }

    return (
      practiceTestDetailData.creator_type?.toLocaleLowerCase() !== 'student'
    )
  }, [practiceTestDetailData])

  const handleDeactivate = async () => {
    try {
      await deactivatePracticeTest(ptId)
      toast.success('Mock exam deactivated', {
        duration: 3000,
        position: 'top-right',
      })
      navigate({
        to: '/test/$moduleId',
        params: { moduleId },
      })
    } catch (e) {
      const message =
        e instanceof Error ? e.message : 'Failed to deactivate mock exam'
      toast.error(message, { duration: 3000, position: 'top-right' })
    }
  }

  const handleActivate = async () => {
    try {
      await activatePracticeTest(ptId)
      toast.success('Mock exam activated', {
        duration: 3000,
        position: 'top-right',
      })
      navigate({
        to: '/test/$moduleId',
        params: { moduleId },
      })
    } catch (e) {
      const message =
        e instanceof Error ? e.message : 'Failed to activate mock exam'
      toast.error(message, { duration: 3000, position: 'top-right' })
    }
  }

  function handleOpenDuplicate() {
    setIsDuplicateOpen(true)
  }

  async function handleConfirmDuplicate() {
    try {
      const res = await duplicatePracticeTest({
        practice_test_id: Number(ptId),
        practice_test_name: practiceTestDetailData?.pt_name || '',
        general_information: practiceTestDetailData?.general_information || '',
        class_id: [],
        user_id: Number(user?.id),
        module_id: Number(moduleId),
      })

      toast.success(`Duplicated mock exam`, {
        duration: 3000,
        position: 'top-right',
      })

      navigate({
        to: '/create-mock-exam/detail/$ptId/$moduleId',
        params: { ptId: res?.[0]?.id, moduleId },
      })
      setIsDuplicateOpen(false)
    } catch (e) {
      const message =
        e instanceof Error ? e.message : 'Failed to duplicate practice test'
      toast.error(message, { duration: 3000, position: 'top-right' })
    }
  }

  return (
    <Stack gap={'24px'} className=''>
      <HeaderEdit
        route='/test/$moduleId'
        params={{ moduleId }}
        className='bg-[#00397A]'
      >
        <Hstack gap={'12px'} className='p-[10px]'>
          <Button
            className={`bg-[#FFFFFF26] text-white hover:bg-[#FFFFFF26] ${hoverPressable}`}
            onClick={() => setIsPreviewOpen(true)}
            disabled={!practiceTestDetailData}
          >
            Preview
          </Button>
          {practiceTestDetailData?.status === PracticeTestStatus.PUBLISHED ? (
            <Button
              disabled={!canDuplicateTest}
              className={`border bg-red-700 text-white hover:bg-transparent ${hoverPressable}`}
              onClick={() => setIsConfirmActiveOpen(true)}
            >
              <IconX />
              Deactivate
            </Button>
          ) : (
            <Button
              disabled={!canDuplicateTest}
              className={`border bg-[#47CD89] text-black hover:bg-transparent hover:text-white ${hoverPressable}`}
              onClick={() => setIsConfirmActiveOpen(true)}
            >
              Activate
            </Button>
          )}

          <Button
            disabled={!canDuplicateTest}
            className={`border border-[##FFFFFF] bg-[#D4D4D4D1] text-[#002652] hover:bg-transparent hover:text-white ${hoverPressable}`}
            onClick={handleOpenDuplicate}
          >
            Duplicate
          </Button>
        </Hstack>
      </HeaderEdit>
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className='md:max-w-5xl lg:max-w-6xl'>
          <div className='flex items-center justify-between'>
            <h3 className='text-[20px] font-medium text-[#002652]'>
              Preview: {practiceTestDetailData?.pt_name || 'Mock Exam'}
            </h3>
            <button
              className='text-[#475467] hover:text-[#002652]'
              onClick={() => setIsPreviewOpen(false)}
            ></button>
          </div>
          <div className='max-h-[70vh] overflow-auto rounded-md border bg-white'>
            <div className='p-4'>
              <Preview
                practiceTestData={practiceTestDetailData as any}
                doingTime={totalDoingTimeSec}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={isDuplicateOpen} onOpenChange={setIsDuplicateOpen}>
        <DialogContent className='px-0 pt-5 sm:max-w-2xl'>
          <Stack gap={'16px'}>
            <Hstack
              justify={'between'}
              className='border-b border-[#E5E5E5] pb-[25px]'
            >
              <h3 className='px-6 text-[20px] font-medium text-[#002652]'>
                Duplicate {practiceTestDetailData?.pt_name || 'Practice Test'}
              </h3>
            </Hstack>

            <div className='flex w-full flex-col items-center gap-10 text-sm text-[#002652]'>
              <p>Are you sure to duplicate this test?</p>
              <p>Duplicated test will be in Draft.</p>
            </div>
            <Hstack justify={'end'} gap={'8px'} className='px-6'>
              <Button
                className='border border-[#EAECF0] bg-white text-[#475467] hover:bg-transparent'
                onClick={() => setIsDuplicateOpen(false)}
              >
                Cancel
              </Button>
              <Button
                className='min-w-[94px] text-white'
                onClick={handleConfirmDuplicate}
                disabled={isDuplicating}
              >
                {isDuplicating ? <Spinner /> : <>Duplicate</>}
              </Button>
            </Hstack>
          </Stack>
        </DialogContent>
      </Dialog>
      <DetailView
        isLoadingPracTestDetail={isLoadingPracTestStat}
        pracTestStat={pracTestStat as PracTestDetailRes}
        practiceTestDetailData={practiceTestDetailData as PracticeTestDetail}
        setPage={_setPage}
        isLoading={isLoading}
        // setSize={setSize}
      />

      <ConfirmDialog
        open={isConfirmActiveOpen}
        onOpenChange={setIsConfirmActiveOpen}
        handleClose={() => setIsConfirmActiveOpen(false)}
        title={
          <div className='flex w-full justify-center'>
            <div className='flex h-20 w-20 items-center justify-center rounded-full bg-gray-100 text-gray-600'>
              <IconInfoCircle size={40} />
            </div>
          </div>
        }
        desc={
          <div className='text-center'>
            <p className='text-[#171717]'>
              {`You are about to ${practiceTestDetailData?.status === PracticeTestStatus.PUBLISHED ? 'deactivate' : 'activate'} 
              this test for all classes in ${practiceTestDetailData?.module_name}.`}
            </p>
            {`Are you sure to ${practiceTestDetailData?.status === PracticeTestStatus.PUBLISHED ? 'deactivate' : 'activate'} this test?`}
          </div>
        }
        cancelBtnText='Cancel'
        confirmText={
          practiceTestDetailData?.status === PracticeTestStatus.PUBLISHED
            ? 'Deactive'
            : 'Active'
        }
        isLoading={isDeactiving || isActiving}
        handleConfirm={() => {
          if (practiceTestDetailData?.status === PracticeTestStatus.PUBLISHED) {
            handleDeactivate()
            return
          }

          handleActivate()
        }}
        className='max-w-md'
      />
      <div className='hidden' />
    </Stack>
  )
}
